import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, ArrowDown, Mail, Crown, Eye, KeyRound, RefreshCw } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, badge: "bg-[#1E2347] text-[#7C3AED]" },
  sub_admin: { label: "Sub-admin", icon: Crown, badge: "bg-[#3B82F6] text-white" },
  supervisor: { label: "Supervisore", icon: Eye, badge: "bg-purple-100 text-purple-800 border border-purple-200" },
};

export default function SubAdminsPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "" });
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => setUsers((await api.get("/admin/sub-admins")).data);
  useEffect(() => { load(); }, []);

  const promote = async (email) => {
    if (!window.confirm(`Promuovere ${email} a sub-amministratore?`)) return;
    try {
      await api.post("/admin/sub-admins/promote", { email });
      toast.success("Promosso a sub-amministratore");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const demote = async (email) => {
    if (!window.confirm(`Rimuovere il ruolo di sub-amministratore a ${email}?`)) return;
    try {
      await api.post("/admin/sub-admins/demote", { email });
      toast.success("Riportato ad admin");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const demoteSupervisor = async (email) => {
    if (!window.confirm(`Rimuovere il ruolo di Supervisore a ${email}? Tornerà a dipendente normale.`)) return;
    try {
      await api.post("/admin/supervisors/demote", { email });
      toast.success(`${email} torna dipendente`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const resetUserPwd = async (email) => {
    const newPwd = window.prompt(`Imposta una nuova password per ${email}\n(min 4 caratteri):`, "");
    if (!newPwd) return;
    if (newPwd.length < 4) { toast.error("Password troppo corta"); return; }
    try {
      await api.post("/admin/users/reset-password", { email, new_password: newPwd });
      toast.success(`Password reimpostata per ${email}`);
      window.alert(`✅ Password aggiornata.\n\nComunica all'utente:\n• Email: ${email}\n• Password provvisoria: ${newPwd}\n\nL'utente può cambiarla dopo il login dalla sezione Profilo.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const bulkResetSupervisors = async () => {
    const supervisors = users.filter(u => u.role === "supervisor");
    if (supervisors.length === 0) {
      toast.info("Nessun Supervisore da resettare");
      return;
    }
    const newPwd = window.prompt(
      `Reset MASSIVO della password per ${supervisors.length} Supervisori:\n\n${supervisors.map(s => "• " + s.email).join("\n")}\n\nNuova password comune (min 4 caratteri):`,
      "Supervisore2026!"
    );
    if (!newPwd) return;
    if (newPwd.length < 4) { toast.error("Password troppo corta"); return; }
    try {
      const r = await api.post("/admin/users/bulk-reset-password", {
        emails: supervisors.map(s => s.email),
        new_password: newPwd,
      });
      toast.success(`${r.data.modified}/${r.data.requested} password aggiornate`);
      window.alert(`✅ ${r.data.modified} password aggiornate.\n\nPassword comune: ${newPwd}\n\nComunica agli utenti che possono accedere ora.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset massivo");
    }
  };

  const invite = async () => {
    try {
      const r = await api.post("/admin/sub-admins/invite", form);
      toast.success(`Invitato · password provvisoria: ${r.data.temp_password}`);
      setOpen(false); setForm({ email: "", full_name: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const counts = useMemo(() => ({
    all: users.length,
    admin: users.filter(u => u.role === "admin").length,
    sub_admin: users.filter(u => u.role === "sub_admin").length,
    supervisor: users.filter(u => u.role === "supervisor").length,
  }), [users]);

  const filtered = useMemo(() =>
    roleFilter === "all" ? users : users.filter(u => u.role === roleFilter),
  [users, roleFilter]);

  return (
    <div className="p-4 md:p-12" data-testid="subadmins-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Gestione team</div>
          <h1 className="font-display text-4xl">Sub-amministratori & Supervisori</h1>
          <p className="text-[#555] mt-1 text-sm">Promuovi admin a sub-amministratori (possono generare QR e inviare comunicazioni) o consulta i Supervisori (read-only).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {counts.supervisor > 0 && (
            <Button
              onClick={bulkResetSupervisors}
              variant="outline"
              data-testid="bulk-reset-supervisors-btn"
              className="rounded-full h-11 border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <RefreshCw className="w-4 h-4 mr-2"/> Reset password tutti i Supervisori ({counts.supervisor})
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-subadmin-btn" className="rounded-full bg-[#3B82F6] hover:bg-black h-11 btn-glow">
                <UserPlus className="w-4 h-4 mr-2"/> Invita sub-admin
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl border-0 max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo sub-amministratore</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome completo</Label><Input className="rounded-xl mt-1" value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})}/></div>
                <div><Label>Email *</Label><Input type="email" className="rounded-xl mt-1" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value.toLowerCase()})} placeholder="referente@azienda.it"/></div>
                <div className="bg-[#1E2347] border border-[#3B82F6]/30 rounded-xl p-3 text-xs text-[#444]">
                  Riceverà email con password provvisoria. Potrà cambiare al primo accesso.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={invite} className="rounded-full bg-[#3B82F6] hover:bg-black">Invita</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filtri ruolo */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Label className="text-xs uppercase tracking-widest text-[#757575] font-bold">Filtra per ruolo</Label>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="rounded-full w-56" data-testid="sa-role-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti ({counts.all})</SelectItem>
            <SelectItem value="admin">Solo Admin ({counts.admin})</SelectItem>
            <SelectItem value="sub_admin">Solo Sub-admin ({counts.sub_admin})</SelectItem>
            <SelectItem value="supervisor">Solo Supervisori ({counts.supervisor})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#1F2540]">
            <tr className="text-left">
              <th className="p-4 font-semibold">Nome</th>
              <th className="p-4 font-semibold">Email</th>
              <th className="p-4 font-semibold">Ruolo</th>
              <th className="p-4 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="4" className="p-12 text-center text-[#757575]">Nessun utente per il filtro selezionato.</td></tr>}
            {filtered.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.admin;
              const Icon = meta.icon;
              return (
                <tr key={u.id} className="border-b border-[#1F2540] hover:bg-[#1E2347]/30" data-testid={`sa-${u.id}`}>
                  <td className="p-4 font-semibold">{u.full_name}</td>
                  <td className="p-4 text-[#555]"><Mail className="w-3 h-3 inline mr-1"/>{u.email}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${meta.badge}`}>
                      <Icon className="w-3 h-3"/> {meta.label}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={()=>resetUserPwd(u.email)}
                      data-testid={`reset-pwd-${u.id}`}
                      className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50"
                      title="Reimposta password"
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1"/> Reset password
                    </Button>
                    {u.role === "sub_admin" && (
                      <Button size="sm" variant="outline" onClick={()=>demote(u.email)} className="rounded-full" data-testid={`demote-${u.id}`}>
                        <ArrowDown className="w-3.5 h-3.5 mr-1"/> Rimuovi sub-admin
                      </Button>
                    )}
                    {u.role === "admin" && (
                      <Button size="sm" onClick={()=>promote(u.email)} className="rounded-full bg-[#3B82F6] hover:bg-black" data-testid={`promote-${u.id}`}>
                        <Crown className="w-3.5 h-3.5 mr-1"/> Promuovi a sub-admin
                      </Button>
                    )}
                    {u.role === "supervisor" && (
                      <Button size="sm" variant="outline" onClick={()=>demoteSupervisor(u.email)} className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`demote-sup-${u.id}`}>
                        <ArrowDown className="w-3.5 h-3.5 mr-1"/> Rimuovi Supervisore
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
